import React, { useState, useRef, useEffect, useCallback, useReducer } from 'react';
import { io, Socket } from 'socket.io-client';
import { Send, Bot, RotateCcw, Pin, PinOff, History, AlertTriangle, Eraser, X, MoreHorizontal } from 'lucide-react';
import { universalLexClient, LexContext, LexMessage, LexResponse } from '../../services/universalLexClient';
import { useProactiveContext } from './hooks/useProactiveContext';
import { useKeycloak } from '@react-keycloak/web';
import { LexMessageRenderer } from './LexMessageRenderer';
import { LexActionCard, type LexAction } from './LexActionCard';
import { ConversationHistoryModal } from './ConversationHistoryModal';
import LexFeedbackButtons from '@/features/chat/components/LexFeedbackButtons';

interface UniversalLexAssistantProps {
  context: LexContext;
  isOpen?: boolean;
  isPinned?: boolean;
  onToggleOpen?: () => void;
  onTogglePin?: () => void;
  className?: string;
  variant?: 'sidebar' | 'modal' | 'inline';
  placeholder?: string;
  hideHeader?: boolean;
  externalMessages?: LexMessage[]; // For injecting LDSP analysis and other external messages
}

/**
 * Session Switch Confirmation Dialog
 */
interface SessionSwitchConfirmDialogProps {
  isOpen: boolean;
  onSaveAndSwitch: () => void;
  onDiscardAndSwitch: () => void;
  onCancel: () => void;
}

const SessionSwitchConfirmDialog: React.FC<SessionSwitchConfirmDialogProps> = ({
  isOpen,
  onSaveAndSwitch,
  onDiscardAndSwitch,
  onCancel
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Unsaved Changes</h3>
        </div>

        <p className="text-gray-600 dark:text-gray-400 mb-6">
          You have unsaved changes in this session. What would you like to do?
        </p>

        <div className="flex flex-col space-y-3">
          <button
            onClick={onSaveAndSwitch}
            className="w-full px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover focus:outline-none focus:ring-2 focus:ring-lex-accent focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
          >
            Save & Switch
          </button>

          <button
            onClick={onDiscardAndSwitch}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
          >
            Discard & Switch
          </button>

          <button
            onClick={onCancel}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-lex-accent focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Universal Lex Assistant - THE single Lex component used across all EdgeLex modules
 * 
 * This component provides a consistent Lex experience everywhere:
 * - DMS documents, Matters, Billing, Calendar, Contacts, Admin
 * - Maintains conversation continuity across modules
 * - Context-aware responses based on current work
 * - Unified UI/UX with module-specific adaptations
 */
export const UniversalLexAssistant: React.FC<UniversalLexAssistantProps> = ({
  context,
  isOpen = true,
  isPinned = false,
  onToggleOpen,
  onTogglePin,
  className = '',
  variant = 'sidebar',
  placeholder,
  hideHeader = false,
  externalMessages = []
}) => {
  const { keycloak } = useKeycloak();
  const [messages, setMessages] = useState<LexMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // Live partial status stream
  // NOTE: kept for timing; intentionally unused in JSX
  const [liveStatusMessage, setLiveStatusMessage] = useState<string | null>(null);
  const [liveProgress, setLiveProgress] = useState<number | undefined>(undefined);
  // No-op reference to satisfy strict linters while preserving state
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  liveStatusMessage;
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  liveProgress;
  const liveStatusMessageRef = useRef<string | null>(null);
  const liveProgressRef = useRef<number | undefined>(undefined);
  const [, forceRerender] = useReducer((x: number) => x + 1, 0);
  // Developer flag (kept for future toggles)
  // const statusDebugFlag = (typeof window !== 'undefined' && (window as any).__LEX_STATUS_DEBUG === true);
  // WebSocket visual status indicator
  const [wsConnected, setWsConnected] = useState<boolean>(false);
  const [wsJoinedSessions, setWsJoinedSessions] = useState<string[]>([]);
  const socketRef = useRef<Socket | null>(null);
  // Refs to avoid stale closures inside socket handlers
  const wsJoinedSessionsRef = useRef<string[]>([]);
  const currentSessionIdRef = useRef<string | null>(null);
  const currentConversationIdRef = useRef<string | null>(null);
  const statusMessageIdRef = useRef<string | null>(null);
  const statusClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusInactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStatusTsRef = useRef<number>(0);
  const lastStatusDisplayRef = useRef<{ text: string; progress?: number; ts: number } | null>(null);
  const clearGenRef = useRef<number>(0);

  // Create or update the in-stream ephemeral status bubble
  const upsertStatusBubble = useCallback((text: string, progress?: number) => {
    // De-dup within 500ms for identical content
    const now = Date.now();
    const last = lastStatusDisplayRef.current;
    if (last && last.text === text && last.progress === progress && (now - last.ts) < 500) {
      return; // Skip redundant update
    }
    lastStatusDisplayRef.current = { text, progress, ts: now };
    setMessages(prev => {
      const id = statusMessageIdRef.current || `status-${Date.now()}`;
      if (!statusMessageIdRef.current) statusMessageIdRef.current = id;
      const exists = prev.some(m => m.id === id);
      const updated: LexMessage = {
        id,
        sender: 'lex',
        message: text,
        timestamp: new Date(),
        // Store progress in metadata under a generic key to avoid type widening
        metadata: { intent: 'status', confidence: 1, ...(typeof progress === 'number' ? { progress } : {}) }
      };
      return exists ? prev.map(m => (m.id === id ? updated : m)) : [...prev, updated];
    });
  }, []);

  // Local animated status bubble renderer
  const StatusBubble: React.FC<{ text: string; progress?: number }> = ({ text, progress }) => {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
      const t = setTimeout(() => setVisible(true), 0);
      return () => clearTimeout(t);
    }, [text, progress]);
    return (
      <div className={`bg-lex-warning-bg border border-lex-warning/20 rounded-lg shadow-sm px-3 py-2 max-w-xs transition-all duration-200 transform ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}>
        <div className="flex items-center space-x-2 text-lex-warning text-sm">
          <div className="w-5 h-5 bg-lex-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Bot className="w-3.5 h-3.5" />
          </div>
          <span className="whitespace-pre-wrap font-medium">{text}</span>
          {typeof progress === 'number' && (
            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded bg-white/50 dark:bg-gray-800/50 border border-lex-warning/30 text-[10px] leading-none font-semibold">
              {progress}%
            </span>
          )}
        </div>
      </div>
    );
  };

  const clearStatusBubble = useCallback(() => {
    setMessages(prev => prev.filter(m => m.metadata?.intent !== 'status'));
    statusMessageIdRef.current = null;
    setLiveStatusMessage(null);
    setLiveProgress(undefined);
    liveStatusMessageRef.current = null;
    liveProgressRef.current = undefined;
  }, []);

  // Graceful status clearing after final response
  const scheduleClearStatusGraceful = useCallback((graceMs: number = 1200) => {
    if (statusClearTimeoutRef.current) clearTimeout(statusClearTimeoutRef.current);
    statusClearTimeoutRef.current = setTimeout(() => {
      if (Date.now() - lastStatusTsRef.current >= graceMs - 100) {
        clearStatusBubble();
      }
    }, graceMs);
  }, [clearStatusBubble]);

  // Show/update status and auto-hide after inactivity
  const showStatus = useCallback((text: string, progress?: number) => {
    lastStatusTsRef.current = Date.now();
    const prevText = liveStatusMessageRef.current;
    const prevProg = liveProgressRef.current;
    if (text !== prevText) {
      setLiveStatusMessage(text);
      liveStatusMessageRef.current = text;
    }
    if (progress !== prevProg) {
      setLiveProgress(progress);
      liveProgressRef.current = progress;
    }
    if (text === prevText && progress === prevProg) {
      // No visible delta, still ensure a render so bubble stays fresh
      forceRerender();
    }
    if (statusInactivityTimeoutRef.current) clearTimeout(statusInactivityTimeoutRef.current);
    statusInactivityTimeoutRef.current = setTimeout(() => {
      if (Date.now() - lastStatusTsRef.current >= 2900) {
        clearStatusBubble();
      }
    }, 3000);
  }, [clearStatusBubble]);
  
  // Streaming response state
  const streamingTurnIdRef = useRef<string | null>(null);
  const streamingContentRef = useRef<string>('');
  const [streamingContent, setStreamingContent] = useState<string>('');

  // Session state tracking for unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoadedSession, setIsLoadedSession] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSessionLoad, setPendingSessionLoad] = useState<{sessionId: string, conversationId: string} | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Initialize Lex context and load conversation history when token and context are ready
  useEffect(() => {
    if (!keycloak.token) {
      try { (window as any).__lexLastSocketAuth = { reason: 'skip_init_no_token', ts: Date.now() }; } catch {}
      return;
    }

    if (socketRef.current) {
      return;
    }

    const sessionId = (context as any)?.sessionId || universalLexClient.getConversationId();

    socketRef.current = io('/', {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token: keycloak.token },
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    if (!socketRef.current.connected) {
      try { socketRef.current.connect(); } catch {}
    }

    socketRef.current.onAny((event: string, ...args: any[]) => {
      const maybe = args?.[0];
      if (
        event === 'lex:partial_status' ||
        (typeof maybe === 'object' && maybe?.type === 'lex:partial_status') ||
        (typeof event === 'string' && event.includes('partial_status'))
      ) {
        const payload = typeof maybe?.payload === 'object' ? maybe.payload : (maybe || {});
        const msg = payload?.message;
        const prog = typeof payload?.progress === 'number' ? payload.progress : undefined;
        if (msg) {
          showStatus(msg, prog);
          upsertStatusBubble(msg, prog);
        }
        return;
      }
      if (typeof event === 'string') {
        if (event.startsWith('llm:call:started')) {
          showStatus('Analyzing…', 35);
          upsertStatusBubble('Analyzing…', 35);
        } else if (event.includes('trace') || event.includes('intermediate')) {
          showStatus('Refining the response…', 65);
          upsertStatusBubble('Refining the response…', 65);
        } else if (event.startsWith('llm:call:completed')) {
          showStatus('Finalizing…', 95);
          upsertStatusBubble('Finalizing…', 95);
        }
      }
    });

    socketRef.current.on('connect', () => {
      setWsConnected(true);
      currentSessionIdRef.current = sessionId ?? null;
      currentConversationIdRef.current = universalLexClient.getConversationId();
      if (sessionId) {
        socketRef.current?.emit('join-lex-session', sessionId, () => {});
      }
      const convId = universalLexClient.getConversationId();
      if (convId && convId !== sessionId) {
        socketRef.current?.emit('join-lex-session', convId, () => {});
      }
    });

    socketRef.current.on('lex:rooms:joined', (payload: { room: string; sessionId: string }) => {
      setWsConnected(true);
      setWsJoinedSessions(prev => {
        const next = Array.from(new Set([payload.sessionId, ...prev]));
        wsJoinedSessionsRef.current = next;
        return next;
      });
    });
    socketRef.current.on('lex:rooms:left', (payload: { room: string; sessionId: string }) => {
      setWsJoinedSessions(prev => {
        const next = prev.filter(id => id !== payload.sessionId);
        wsJoinedSessionsRef.current = next;
        return next;
      });
    });

    socketRef.current.on('lex:partial_status', (evt: any) => {
      const payload = typeof evt?.payload === 'object' ? evt.payload : evt;
      const msg: string | undefined = payload?.message;
      const prog: number | undefined = typeof payload?.progress === 'number' ? payload.progress : undefined;
      if (!msg) return;
      showStatus(msg, prog);
      upsertStatusBubble(msg, prog);
    });

    // Streaming response chunks — progressive text reveal
    socketRef.current.on('lex:stream:chunk', (evt: any) => {
      const { turnId, accumulatedContent, isComplete } = evt || {};
      if (!turnId || !accumulatedContent) return;

      streamingTurnIdRef.current = turnId;
      streamingContentRef.current = accumulatedContent;
      setStreamingContent(accumulatedContent);

      // Replace status bubble with streaming message
      const streamId = `stream-${turnId}`;
      setMessages(prev => {
        // Remove any status bubbles and update/add streaming message
        const withoutStatus = prev.filter(m => m.metadata?.intent !== 'status');
        const exists = withoutStatus.some(m => m.id === streamId);
        const streamMsg: LexMessage = {
          id: streamId,
          sender: 'lex',
          message: accumulatedContent,
          timestamp: new Date(),
          metadata: { intent: isComplete ? 'stream_complete' : 'streaming', confidence: 1 }
        };
        return exists
          ? withoutStatus.map(m => m.id === streamId ? streamMsg : m)
          : [...withoutStatus, streamMsg];
      });
    });

    socketRef.current.on('disconnect', () => {
      setLiveStatusMessage(null);
      setLiveProgress(undefined);
      setWsConnected(false);
    });

    socketRef.current.on('connect_error', () => {
      setWsConnected(false);
    });

    universalLexClient.setContext(context, keycloak.token);
    (async () => {
      try {
        const history = await universalLexClient.loadConversationHistory(keycloak.token);
        if (history.length > 0) setMessages(history);
      } catch {}
    })();

    return () => {
      try {
        const sid = (context as any)?.sessionId || universalLexClient.getConversationId();
        const cid = universalLexClient.getConversationId();
        socketRef.current?.emit('leave-lex-session', sid);
        if (cid && cid !== sid) {
          socketRef.current?.emit('leave-lex-session', cid);
        }
        socketRef.current?.disconnect();
      } catch {}
      socketRef.current = null;
      setLiveStatusMessage(null);
      setLiveProgress(undefined);
      clearStatusBubble();
      setWsConnected(false);
      setWsJoinedSessions([]);
    };
  }, [keycloak.token]);

  // Ensure socket attempts connection when the widget is opened with a ready token
  useEffect(() => {
    if (!isOpen) return;
    if (!keycloak.token) return;
    // If socket exists but is not connected yet, refresh token and connect
    (async () => {
      try {
        const maybeUpdate = (keycloak as any)?.updateToken as undefined | ((minValidity: number) => Promise<boolean>);
        if (maybeUpdate) { await maybeUpdate(30); }
      } catch {}
      if (socketRef.current && !socketRef.current.connected) {
        try { (socketRef.current as any).io.opts.auth = { token: keycloak.token }; } catch {}
        try { socketRef.current.connect(); } catch {}
      }
    })();
  }, [isOpen, keycloak.token]);

  // On context changes, (re)join the relevant rooms without rebuilding the socket
  useEffect(() => {
    try {
      const sessId = (context as any)?.sessionId || universalLexClient.getConversationId();
      const convId = universalLexClient.getConversationId();
      currentSessionIdRef.current = ((context as any)?.sessionId || currentSessionIdRef.current) ?? null;
      currentConversationIdRef.current = convId ?? null;
      if (socketRef.current && socketRef.current.connected) {
        if (sessId) socketRef.current.emit('join-lex-session', sessId, (res: { room: string; sessionId: string }) => console.log('[Lex Socket] ack joined (ctx sess)', res));
        if (convId && convId !== sessId) socketRef.current.emit('join-lex-session', convId, (res: { room: string; sessionId: string }) => console.log('[Lex Socket] ack joined (ctx conv)', res));
      }
    } catch {}
  }, [context]);

  // Merge external messages (like LDSP analysis) into the conversation
  useEffect(() => {
    if (externalMessages.length > 0) {
      console.log('🔗 [UniversalLexAssistant] Adding external messages to conversation:', externalMessages.length);
      setMessages(prev => {
        // Avoid duplicates by checking message IDs
        const existingIds = new Set(prev.map(msg => msg.id));
        const newMessages = externalMessages.filter(msg => !existingIds.has(msg.id));
        
        if (newMessages.length > 0) {
          // Sort all messages by timestamp to maintain chronological order
          const allMessages = [...prev, ...newMessages].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          return allMessages;
        }
        return prev;
      });
    }
  }, [externalMessages]);

  // Auto-scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Generate contextual placeholder text
  const getContextualPlaceholder = (): string => {
    if (placeholder) return placeholder;
    
    switch (context.application) {
      case 'documents':
        return context.documentId 
          ? 'Ask me about this document...' 
          : 'Ask me about your documents...';
      case 'matters':
        return context.matterId 
          ? 'Ask me about this matter...' 
          : 'Ask me about your matters...';
      case 'billing':
        return 'Ask me about billing and time entries...';
      case 'calendar':
        return 'Ask me about your calendar and appointments...';
      case 'contacts':
        return 'Ask me about clients and contacts...';
      case 'admin':
        return 'Ask me about firm administration...';
      default:
        return 'Ask me anything about your legal work...';
    }
  };

  // Get module-specific Lex personality greeting with LDSP awareness
  const getContextualGreeting = (): string => {
    // 🎯 LDSP AWARENESS: Check if real-time AI features are enabled
    const hasLDSPValidation = context.pluginIds?.includes('ldsp-validation');
    const hasLDSPCompletion = context.pluginIds?.includes('ldsp-completion');
    const hasLDSPFeatures = hasLDSPValidation || hasLDSPCompletion;

    switch (context.application) {
      case 'documents':
        if (hasLDSPFeatures) {
          const features = [];
          if (hasLDSPValidation) features.push('real-time validation');
          if (hasLDSPCompletion) features.push('smart completion');
          return `I see you want me to provide Realtime drafting feedback. Ready when you are! I have ${features.join(' and ')} active to assist your legal writing.`;
        }
        return "I'm ready to help you analyze documents, extract key information, and answer questions about your legal files.";
      case 'matters':
        return "I'm here to help you manage matters, track progress, analyze case strategy, and coordinate legal work.";
      case 'billing':
        return "I can help you with time tracking, billing analysis, invoice management, and financial reporting.";
      case 'calendar':
        return "I'm ready to assist with scheduling, deadline management, court date tracking, and calendar coordination.";
      case 'contacts':
        return "I can help you manage client relationships, contact information, and communication history.";
      case 'admin':
        return "I'm here to help with firm administration, user management, and system configuration.";
      default:
        return "I'm Lex, your AI legal assistant. I'm here to help you with all aspects of your legal practice.";
    }
  };

  // Send message to Universal Lex Orchestrator
  const handleSendMessage = async (overrideMessage?: string) => {
    const messageText = overrideMessage || inputMessage.trim();
    if (!messageText || isTyping) return;

    // Ensure we are joined to the latest conversation room before sending (with ack logs)
    try {
      // Refresh client context to capture the most recent sessionId from props
      try {
        if (keycloak.token) {
          universalLexClient.setContext(context, keycloak.token);
        }
      } catch {}
      const convId = universalLexClient.getConversationId();
      const sessId = (context as any)?.sessionId;
      if (socketRef.current && convId) {
        socketRef.current.emit('join-lex-session', convId, (res: { room: string; sessionId: string }) => {
          console.log('[Lex Socket] ack joined before send (conv)', res);
        });
      }
      if (socketRef.current && sessId) {
        socketRef.current.emit('join-lex-session', sessId, (res: { room: string; sessionId: string }) => {
          console.log('[Lex Socket] ack joined before send (sess)', res);
        });
      }
    } catch {}

    const userMessage: LexMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      message: messageText,
      timestamp: new Date(),
      metadata: {
        moduleContext: {
          application: context.application,
          currentView: context.currentView
        },
        documentContext: context.documentId ? {
          documentId: context.documentId,
          documentType: context.documentType,
          documentName: context.documentId
        } : undefined,
        matterContext: context.matterId ? {
          matterId: context.matterId,
          clientId: context.clientId
        } : undefined
      }
    };

    setMessages(prev => [...prev, userMessage]);
    if (!overrideMessage) setInputMessage('');
    setIsTyping(true);
    const sendGen = clearGenRef.current;

    try {
      const response: LexResponse = await universalLexClient.sendMessage(messageText, keycloak.token);

      // If the user cleared the chat while the request was in flight, discard the response
      if (clearGenRef.current !== sendGen) {
        console.log('🗑️ [UniversalLexAssistant] Discarding response — chat was cleared during request');
        return;
      }

      const telData = response.telemetry as Record<string, any> | undefined;
      const lexMessage: LexMessage = {
        id: `lex-${Date.now()}`,
        sender: 'lex',
        message: response.message,
        timestamp: new Date(),
        metadata: {
          intent: response.metadata?.intent,
          confidence: response.metadata?.confidence,
          actions: response.actions,
          intentCategory: telData?.intentCategory || telData?.intent_category || response.intent,
          toolId: telData?.toolId,
          toolSuccess: telData?.toolSuccess ?? (telData?.toolId ? true : undefined),
        }
      };

      // Replace any streaming message and status bubbles with the final response (which carries full metadata/actions)
      const streamId = streamingTurnIdRef.current ? `stream-${streamingTurnIdRef.current}` : null;
      setMessages(prev => {
        const cleaned = prev.filter(m =>
          m.metadata?.intent !== 'status' &&
          m.metadata?.intent !== 'streaming' &&
          m.metadata?.intent !== 'stream_complete' &&
          m.id !== streamId
        );
        return [...cleaned, lexMessage];
      });
      // Reset streaming state
      streamingTurnIdRef.current = null;
      streamingContentRef.current = '';
      setStreamingContent('');
      scheduleClearStatusGraceful(1800);
      
      // Track unsaved changes if this is a loaded session
      if (isLoadedSession) {
        setHasUnsavedChanges(true);
      }
      
      console.log('✅ [UniversalLexAssistant] Message exchange completed:', {
        intent: response.metadata?.intent,
        confidence: response.metadata?.confidence,
        hasActions: response.actions && response.actions.length > 0
      });

    } catch (error) {
      // If chat was cleared during request, don't show the error either
      if (clearGenRef.current !== sendGen) return;

      console.error('🚨 [UniversalLexAssistant] Error sending message:', error);

      const errorMessage: LexMessage = {
        id: `error-${Date.now()}`,
        sender: 'lex',
        message: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
        metadata: {
          intent: 'error',
          confidence: 0
        }
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Start fresh conversation
  const handleFreshConversation = () => {
    universalLexClient.startFreshConversation();
    setMessages([]);
    console.log('🆕 [UniversalLexAssistant] Started fresh conversation');
    // Join new conversation room immediately (with ack)
    try {
      const convId = universalLexClient.getConversationId();
      const sessId = (context as any)?.sessionId;
      if (socketRef.current && convId) socketRef.current.emit('join-lex-session', convId, (res: { room: string; sessionId: string }) => console.log('[Lex Socket] ack joined fresh (conv)', res));
      if (socketRef.current && sessId) socketRef.current.emit('join-lex-session', sessId, (res: { room: string; sessionId: string }) => console.log('[Lex Socket] ack joined fresh (sess)', res));
    } catch {}
  };

  // Handle history modal
  const handleShowHistory = () => {
    setShowHistoryModal(true);
  };

  // Clear chat window (UI only, does not affect backend history or conversationId)
  const handleClearChat = () => {
    void clearChatAndDeleteHistory();
  };

  // Delete active conversation from backend (Redis + DB summary) and clear UI
  const clearChatAndDeleteHistory = async (): Promise<void> => {
    try {
      const activeConversationId = universalLexClient.getConversationId();
      clearGenRef.current++;          // Discard any in-flight LLM responses
      setMessages([]);
      setIsTyping(false);
      setHasUnsavedChanges(false);
      console.log('🧹 [UniversalLexAssistant] Clearing chat and deleting history for conversation:', activeConversationId);

      if (keycloak.authenticated && keycloak.token && activeConversationId) {
        const res = await fetch(`/api/v1/lex-agent/conversation/${activeConversationId}/delete`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${keycloak.token}` }
        });
        if (!res.ok) {
          console.warn('⚠️ [UniversalLexAssistant] Backend conversation delete failed:', res.status);
        } else {
          // Ensure future loads don't resurrect DB history for the same ID
          universalLexClient.startFreshConversation();
          // Ask any open history views to refresh
          try {
            const evt = new CustomEvent('lex:refreshHistory', { detail: {} });
            window.dispatchEvent(evt);
          } catch {}
          console.log('✅ [UniversalLexAssistant] Deleted backend conversation history');
        }
      }
    } catch (err) {
      console.warn('⚠️ [UniversalLexAssistant] Clear chat delete error:', err);
    }
  };

  // Save current session to database
  const saveCurrentSession = async (): Promise<void> => {
    if (!keycloak.authenticated || !keycloak.token || !hasUnsavedChanges) {
      return;
    }

    try {
      console.log('💾 [UniversalLexAssistant] Saving current session...');
      
      // The backend auto-saves conversations via createConversationSummary
      // when messages are sent, so we just need to mark as saved
      setHasUnsavedChanges(false);
      
      console.log('✅ [UniversalLexAssistant] Current session saved successfully');
    } catch (error) {
      console.error('🚨 [UniversalLexAssistant] Error saving current session:', error);
      throw error; // Re-throw to handle in calling function
    }
  };

  // Perform the actual session switch
  const performSessionSwitch = async (sessionId: string, conversationId: string): Promise<void> => {
    if (!keycloak.authenticated || !keycloak.token) {
      console.error('🚨 [UniversalLexAssistant] Cannot load conversation: user not authenticated');
      return;
    }

    try {
      console.log('📄 [UniversalLexAssistant] Switching to conversation:', { sessionId, conversationId });
      
      // Clear current chat messages (destroy current session)
      setMessages([]);
      setInputMessage('');
      
      // Prefer session-scoped loader to avoid cross-session bleed
      let loadedMessages = await universalLexClient.switchToSession(
        context.documentId!,
        sessionId,
        keycloak.token,
        conversationId
      );
      
      // If the session endpoint produced nothing and no fallback available, keep previous behavior
      if (loadedMessages.length === 0 && conversationId) {
        loadedMessages = await universalLexClient.switchToConversation(sessionId, conversationId, keycloak.token);
      }
      
      // Set the loaded messages as the current chat, or show fallback banner if empty
      if (loadedMessages.length === 0) {
        const fallback: LexMessage = {
          id: `info-${Date.now()}`,
          sender: 'lex',
          message: 'Transcript for this session is not available. You can continue the conversation and new messages will be appended to this session.',
          timestamp: new Date(),
          metadata: { intent: 'system_notice', confidence: 1 }
        };
        setMessages([fallback]);
      } else {
        setMessages(loadedMessages);
      }
      
      // Update session state
      setIsLoadedSession(true);
      setHasUnsavedChanges(false);
      
      // Scroll to bottom to show latest messages
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
      console.log('✅ [UniversalLexAssistant] Successfully switched to conversation:', conversationId, 'with', loadedMessages.length, 'messages');
      // Ensure socket is in the correct rooms after switch
      try {
        if (socketRef.current) {
          socketRef.current.emit('join-lex-session', conversationId, (res: { room: string; sessionId: string }) => console.log('[Lex Socket] ack joined switch (conv)', res));
          socketRef.current.emit('join-lex-session', sessionId, (res: { room: string; sessionId: string }) => console.log('[Lex Socket] ack joined switch (sess)', res));
        }
      } catch {}
      
      // Close the history modal
      setShowHistoryModal(false);
      
    } catch (error) {
      console.error('🚨 [UniversalLexAssistant] Error switching to conversation:', error);
      
      // Show user-friendly error message
      const errorMessage: LexMessage = {
        id: `error-${Date.now()}`,
        sender: 'lex',
        message: 'Sorry, I encountered an error loading that conversation. Please try again.',
        timestamp: new Date(),
        metadata: {
          intent: 'error',
          confidence: 0
        }
      };
      
      setMessages([errorMessage]);
    }
  };

  const handleLoadConversation = async (sessionId: string, conversationId: string) => {
    if (!keycloak.authenticated || !keycloak.token) {
      console.error('🚨 [UniversalLexAssistant] Cannot load conversation: user not authenticated');
      return;
    }

    // Check if current session has unsaved changes or any visible messages
    const hasAnyMessages = messages.length > 0;
    // Prompt if there is any visible content or unsaved changes, regardless of loaded state
    if (hasUnsavedChanges || hasAnyMessages) {
      // Store pending session info and show confirmation dialog
      setPendingSessionLoad({ sessionId, conversationId });
      setShowConfirmDialog(true);
      return; // Don't proceed with load yet
    }
    
    // No unsaved changes - proceed directly with session switch
    await performSessionSwitch(sessionId, conversationId);
  };

  // Global event-based session load to centralize Save/Discard logic
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { sessionId: string; conversationId: string } | undefined;
      if (!detail) return;
      void handleLoadConversation(detail.sessionId, detail.conversationId);
    };
    window.addEventListener('lex:loadConversation', handler as EventListener);
    return () => window.removeEventListener('lex:loadConversation', handler as EventListener);
  }, [messages, hasUnsavedChanges, isLoadedSession, keycloak.authenticated, keycloak.token]);

  // Global event: clear chat window (UI only)
  useEffect(() => {
    const clearHandler = () => {
      void clearChatAndDeleteHistory();
    };
    window.addEventListener('lex:clearChat', clearHandler as EventListener);
    return () => window.removeEventListener('lex:clearChat', clearHandler as EventListener);
  }, []);

  // Global event: send message from external source (e.g. Notes context menu)
  const handleSendMessageRef = useRef(handleSendMessage);
  handleSendMessageRef.current = handleSendMessage;
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { message: string } | undefined;
      if (!detail?.message) return;
      handleSendMessageRef.current(detail.message);
    };
    window.addEventListener('lex:sendMessage', handler as EventListener);
    return () => window.removeEventListener('lex:sendMessage', handler as EventListener);
  }, []);

  // Proactive context-awareness: detect module transitions and entity focus changes
  useProactiveContext({
    context,
    isPinned,
    isOpen: isOpen ?? true,
    userName: context.userName || 'there',
    onInjectMessage: (msg) => setMessages(prev => [...prev, msg]),
    onSendBackendMessage: (msg) => handleSendMessageRef.current(msg),
  });

  // Confirmation dialog handlers
  const handleSaveAndSwitch = async () => {
    if (pendingSessionLoad) {
      try {
        await saveCurrentSession();
        await performSessionSwitch(pendingSessionLoad.sessionId, pendingSessionLoad.conversationId);
      } catch (error) {
        console.error('🚨 [UniversalLexAssistant] Error during save and switch:', error);
      } finally {
        setShowConfirmDialog(false);
        setPendingSessionLoad(null);
      }
    }
  };

  const handleDiscardAndSwitch = async () => {
    if (pendingSessionLoad) {
      await performSessionSwitch(pendingSessionLoad.sessionId, pendingSessionLoad.conversationId);
      setShowConfirmDialog(false);
      setPendingSessionLoad(null);
    }
  };

  const handleCancelSwitch = () => {
    setShowConfirmDialog(false);
    setPendingSessionLoad(null);
    // Keep current session active, close history modal
    setShowHistoryModal(false);
  };

  // Get variant-specific styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'modal':
        return {
          container: 'bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-w-2xl mx-auto',
          height: 'h-96'
        };
      case 'inline':
        return {
          container: 'bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700',
          height: 'h-64'
        };
      case 'sidebar':
      default:
        return {
          container: 'bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg',
          height: 'h-full'
        };
    }
  };

  const styles = getVariantStyles();

  // Kebab menu state
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  // Context-aware suggested quick prompts — adapt to module + entity focus
  const getSuggestedPrompts = (): { icon: string; label: string; prompt: string }[] => {
    switch (context.application) {
      case 'matters':
        return context.matterId
          ? [
              { icon: '\u{1F4CB}', label: 'Summarize matter', prompt: 'Summarize this matter for me' },
              { icon: '\u{2696}\uFE0F', label: 'Key dates', prompt: 'What are the key dates and deadlines for this matter?' },
              { icon: '\u{1F4C4}', label: 'Related docs', prompt: 'Show me the related documents for this matter' },
              { icon: '\u{1F4A1}', label: 'Case strategy', prompt: 'What case strategy recommendations do you have?' },
            ]
          : [
              { icon: '\u{1F4CA}', label: 'Open matters', prompt: 'List all my open matters with their current stage and last activity date' },
              { icon: '\u{23F0}', label: 'Overdue tasks', prompt: 'Show me all overdue tasks across all my matters' },
              { icon: '\u{1F4C5}', label: 'This week', prompt: 'What events and deadlines do I have this week across all matters?' },
              { icon: '\u{1F4C8}', label: 'Workload overview', prompt: 'Give me an overview of my current workload across all matters' },
            ];
      case 'contacts':
        return context.clientId
          ? [
              { icon: '\u{1F464}', label: 'Client summary', prompt: 'Give me a summary of this client' },
              { icon: '\u{1F4C2}', label: 'Open matters', prompt: 'What open matters does this client have?' },
              { icon: '\u{1F4B0}', label: 'Billing history', prompt: 'Show me the billing history for this client' },
              { icon: '\u{1F4E8}', label: 'Recent comms', prompt: 'What recent communications have we had with this client?' },
            ]
          : [
              { icon: '\u{1F465}', label: 'Active clients', prompt: 'Show me all active clients' },
              { icon: '\u{1F195}', label: 'Recent leads', prompt: 'Show me recent leads and their status' },
              { icon: '\u{1F50D}', label: 'Find a client', prompt: 'Help me find a specific client or contact' },
              { icon: '\u{1F4CA}', label: 'Client overview', prompt: 'Give me an overview of my client portfolio' },
            ];
      case 'documents':
        return context.documentId
          ? [
              { icon: '\u{1F4DD}', label: 'Summarize', prompt: 'Summarize this document for me' },
              { icon: '\u{1F50D}', label: 'Key terms', prompt: 'Extract key terms and clauses from this document' },
              { icon: '\u{1F4C4}', label: 'Similar docs', prompt: 'Find similar documents in the system' },
              { icon: '\u{2705}', label: 'Compliance', prompt: 'Run a compliance check on this document' },
            ]
          : [
              { icon: '\u{1F4C1}', label: 'Recent uploads', prompt: 'Show me recent document uploads' },
              { icon: '\u{1F50E}', label: 'Search docs', prompt: 'Help me find a specific document' },
              { icon: '\u{1F4CA}', label: 'Doc analytics', prompt: 'Give me an overview of document analytics' },
              { icon: '\u{1F4DD}', label: 'Draft document', prompt: 'Help me draft a new document' },
            ];
      case 'billing':
        return [
          { icon: '\u{23F1}\uFE0F', label: 'Time entries', prompt: 'Show me today\'s time entries' },
          { icon: '\u{1F4B5}', label: 'Unbilled time', prompt: 'What unbilled time do I have?' },
          { icon: '\u{1F9FE}', label: 'Invoices', prompt: 'Show me pending invoices' },
          { icon: '\u{1F4CA}', label: 'Revenue report', prompt: 'Give me a revenue summary' },
        ];
      default:
        return [
          { icon: '\u{1F4AC}', label: 'Get started', prompt: 'What can you help me with?' },
          { icon: '\u{1F50D}', label: 'Search', prompt: 'Help me find something' },
          { icon: '\u{1F4CA}', label: 'Overview', prompt: 'Give me an overview of my work' },
          { icon: '\u{2753}', label: 'Help', prompt: 'What features are available?' },
        ];
    }
  };

  // Show chips after a context transition, even if messages exist.
  // The last message with intent 'context_transition' marks a module switch.
  const showChipsAfterTransition = messages.length > 0 &&
    messages[messages.length - 1]?.metadata?.intent === 'context_transition';

  if (!isOpen) return null;

  return (
    <div className={`${styles.container} ${className} flex flex-col ${styles.height}`}>
      {/* Header — slim, themed */}
      {!hideHeader && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-lex-accent/5 dark:bg-lex-accent/10">
          <div className="flex items-center space-x-2.5">
            <div className="w-7 h-7 bg-lex-accent rounded-full flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 text-lex-accent-contrast" />
            </div>
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Lex AI</h3>
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${wsConnected ? 'bg-lex-success' : 'bg-gray-400'}`} title={wsConnected ? 'Connected' : 'Disconnected'} />
              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{context.application}</span>
            </div>
          </div>

          <div className="flex items-center space-x-1">
            {/* Kebab menu for secondary actions */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                title="More options"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                  <button
                    onClick={() => { handleFreshConversation(); setShowMenu(false); }}
                    className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-2" /> New conversation
                  </button>
                  <button
                    onClick={() => { handleClearChat(); setShowMenu(false); }}
                    className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Eraser className="w-3.5 h-3.5 mr-2" /> Clear chat
                  </button>
                  {context.application === 'documents' && context.documentId && (
                    <button
                      onClick={() => { handleShowHistory(); setShowMenu(false); }}
                      className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <History className="w-3.5 h-3.5 mr-2" /> Conversation history
                    </button>
                  )}
                </div>
              )}
            </div>

            {onTogglePin && (
              <button
                onClick={onTogglePin}
                className={`p-1.5 rounded transition-colors ${isPinned ? 'text-lex-accent bg-lex-accent/10' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                title={isPinned ? 'Unpin Lex' : 'Pin Lex'}
              >
                {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
              </button>
            )}

            {onToggleOpen && (
              <button
                onClick={onToggleOpen}
                className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="Close Lex"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900/50">
            {messages.length === 0 && (
              <div className="flex flex-col items-center pt-8 pb-4">
                <div className="w-12 h-12 bg-lex-accent/10 rounded-full flex items-center justify-center mb-3">
                  <Bot className="w-6 h-6 text-lex-accent" />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">How can I help?</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-[220px] mb-5">
                  {context.matterId || context.documentId
                    ? `Ask me anything about this ${context.application === 'documents' ? 'document' : context.application === 'contacts' ? 'client' : 'matter'}.`
                    : `I can help with your ${context.application}.`}
                </p>
                {/* Suggested prompt chips */}
                <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
                  {getSuggestedPrompts().map((sp, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(sp.prompt)}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs text-left text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-lex-accent hover:text-lex-accent dark:hover:border-lex-accent transition-colors"
                    >
                      <span>{sp.icon}</span>
                      <span className="truncate">{sp.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message, index) => {
              // Find preceding user message for feedback context
              const prevUserMsg = message.sender === 'lex'
                ? messages.slice(0, index).reverse().find((m) => m.sender === 'user')
                : undefined;

              return (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} mb-3`}
              >
                <div className={`max-w-[85%] ${message.sender === 'user' ? '' : 'w-full'}`}>
                  {message.metadata?.intent === 'status' ? (
                    <StatusBubble text={message.message} progress={(message.metadata as any)?.progress} />
                  ) : message.metadata?.intent === 'streaming' ? (
                    /* Streaming message — progressive reveal with blinking cursor */
                    <div className="space-y-2">
                      <LexMessageRenderer
                        content={message.message}
                        className=""
                        enableLegalFormatting={false}
                        enableSmartEmojis={false}
                        role="assistant"
                      />
                      <span className="inline-block w-2 h-4 bg-lex-accent rounded-sm animate-pulse ml-1 align-text-bottom" />
                    </div>
                  ) : message.sender === 'lex' ? (
                    <div className="space-y-2">
                      <LexMessageRenderer
                        content={message.message}
                        className=""
                        enableLegalFormatting={true}
                        enableSmartEmojis={true}
                        role="assistant"
                      />
                      {/* Structured action cards */}
                      <LexActionCard
                        actions={((message?.metadata as any)?.actions || []) as LexAction[]}
                        messageId={message.id}
                        onPromptAction={(prompt) => handleSendMessage(prompt)}
                      />
                      {/* Feedback buttons with pipeline metadata for training classification */}
                      {prevUserMsg && currentConversationIdRef.current && (
                        <LexFeedbackButtons
                          conversationId={currentConversationIdRef.current}
                          messageId={message.id}
                          userQuestion={prevUserMsg.message}
                          responseText={message.message}
                          intentCategory={(message.metadata as any)?.intentCategory}
                          toolId={(message.metadata as any)?.toolId}
                          toolSuccess={(message.metadata as any)?.toolSuccess}
                        />
                      )}
                    </div>
                  ) : (
                    /* User message — accent bubble, right-aligned */
                    <div className="bg-lex-accent text-lex-accent-contrast px-3.5 py-2.5 rounded-2xl rounded-br-sm">
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.message}</p>
                    </div>
                  )}
                </div>
              </div>
              );
            })}

            {/* Re-show suggested prompt chips after a module transition */}
            {showChipsAfterTransition && !isTyping && (
              <div className="flex justify-start mb-3 ml-2">
                <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
                  {getSuggestedPrompts().map((sp, idx) => (
                    <button
                      key={`transition-chip-${idx}`}
                      onClick={() => handleSendMessage(sp.prompt)}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs text-left text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-lex-accent hover:text-lex-accent dark:hover:border-lex-accent transition-colors duration-150"
                    >
                      <span>{sp.icon}</span>
                      <span className="truncate">{sp.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isTyping && !streamingContent && (
              <div className="flex justify-start mb-3">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-1.5 h-1.5 bg-lex-accent rounded-full animate-bounce" />
                      <div className="w-1.5 h-1.5 bg-lex-accent rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                      <div className="w-1.5 h-1.5 bg-lex-accent rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Lex is thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-end space-x-2">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => {
                  setInputMessage(e.target.value);
                  // Auto-grow up to 4 lines
                  const el = e.target;
                  el.style.height = 'auto';
                  el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                    // Reset height
                    const el = e.target as HTMLTextAreaElement;
                    el.style.height = 'auto';
                  }
                }}
                placeholder={getContextualPlaceholder()}
                rows={1}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lex-accent focus:border-transparent text-sm resize-none overflow-hidden"
                disabled={isTyping || !keycloak.authenticated}
                style={{ minHeight: '36px', maxHeight: '96px' }}
              />
              <button
                onClick={() => {
                  handleSendMessage();
                  // Reset textarea height
                  if (inputRef.current) inputRef.current.style.height = 'auto';
                }}
                disabled={!inputMessage.trim() || isTyping || !keycloak.authenticated}
                className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-lex-accent text-lex-accent-contrast rounded-full hover:bg-lex-accent-hover focus:outline-none focus:ring-2 focus:ring-lex-accent focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>

            {!keycloak.authenticated && (
              <p className="text-xs text-lex-danger mt-2">Please log in to use Lex AI Assistant</p>
            )}
          </div>
        </>
      )}

      {/* Conversation History Modal */}
      {context.application === 'documents' && context.documentId && (
        <ConversationHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          documentId={context.documentId}
          documentTitle={context.documentType || 'Document'}
          onLoadConversation={handleLoadConversation}
        />
      )}

      {/* Session Switch Confirmation Dialog */}
      <SessionSwitchConfirmDialog
        isOpen={showConfirmDialog}
        onSaveAndSwitch={handleSaveAndSwitch}
        onDiscardAndSwitch={handleDiscardAndSwitch}
        onCancel={handleCancelSwitch}
      />
    </div>
  );
};

export default UniversalLexAssistant;